import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaSearch } from 'react-icons/fa';
import { EmptyState } from '../../components/EntityList';
import { REPORTS, REPORT_GROUPS } from './reportsCatalogData';
import ReportCard from './ReportCard';

function normalize(value) {
	return value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase();
}

/**
 * Browsable catalog: grouped into the three report sections, rendered as a
 * 2-up (1-up under 640px) grid. The search box that filters it lives above
 * the catalog+panel layout in Reports.jsx, not in here.
 */
function ReportCatalog({ query, selectedId, onSelect }) {
	const filteredReports = useMemo(() => {
		const needle = normalize(query || '');
		if (!needle) return REPORTS;
		return REPORTS.filter((report) => normalize(`${report.title} ${report.subtitle}`).includes(needle));
	}, [query]);

	const groupsWithReports = REPORT_GROUPS.map((group) => ({
		...group,
		reports: filteredReports.filter((report) => report.group === group.id),
	})).filter((group) => group.reports.length > 0);

	return (
		<div>
			{groupsWithReports.length === 0 ? (
				<div className='tw:rounded-panel tw:border tw:border-border'>
					<EmptyState
						icon={<FaSearch />}
						title='Sin coincidencias'
						description='Probá con otro nombre de reporte o revisá la ortografía.'
					/>
				</div>
			) : (
				groupsWithReports.map((group) => (
					<section key={group.id} className='tw:mb-[26px] last:tw:mb-0'>
						<div className='tw:mb-3 tw:flex tw:items-baseline tw:gap-1.5'>
							<h3 className='tw:m-0 tw:text-[12px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-2'>
								{group.title}
							</h3>
							<span className='tw:text-[12px] tw:text-ink-3'>· {group.hint}</span>
						</div>
						<div className='tw:grid tw:grid-cols-1 tw:gap-2.5 tw:min-[640px]:grid-cols-2'>
							{group.reports.map((report) => (
								<ReportCard
									key={report.id}
									report={report}
									selected={report.id === selectedId}
									onSelect={onSelect}
								/>
							))}
						</div>
					</section>
				))
			)}
		</div>
	);
}

ReportCatalog.propTypes = {
	query: PropTypes.string,
	selectedId: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
};

export default ReportCatalog;
